import React, { Suspense } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { getAllTools } from '../data/tools';
import { AdSense } from '../components/Layout/AdSense';
import { InArticleAd } from '../components/Layout/InArticleAd';
import { DisplayAd } from '../components/Layout/DisplayAd';

// Lazy load tool components for code splitting
const WebpToJpg = React.lazy(() => import('../components/Tools/ImageTools/WebpToJpg').then(module => ({ default: module.WebpToJpg })));
const PngToJpg = React.lazy(() => import('../components/Tools/ImageTools/PngToJpg').then(module => ({ default: module.PngToJpg })));
const JpgToPng = React.lazy(() => import('../components/Tools/ImageTools/JpgToPng').then(module => ({ default: module.JpgToPng })));
const ImageCompressor = React.lazy(() => import('../components/Tools/ImageTools/ImageCompressor').then(module => ({ default: module.ImageCompressor })));
const ImageCropper = React.lazy(() => import('../components/Tools/ImageTools/ImageCropper').then(module => ({ default: module.ImageCropper })));
const ImageToBase64 = React.lazy(() => import('../components/Tools/ImageTools/ImageToBase64').then(module => ({ default: module.ImageToBase64 })));
const Base64ToImage = React.lazy(() => import('../components/Tools/ImageTools/Base64ToImage').then(module => ({ default: module.Base64ToImage })));
const ImageColorInverter = React.lazy(() => import('../components/Tools/ImageTools/ImageColorInverter').then(module => ({ default: module.ImageColorInverter })));
const PdfMerger = React.lazy(() => import('../components/Tools/PDFTools/PdfMerger').then(module => ({ default: module.PdfMerger })));
const JpgToPdf = React.lazy(() => import('../components/Tools/PDFTools/JpgToPdf').then(module => ({ default: module.JpgToPdf })));
const PdfToJpg = React.lazy(() => import('../components/Tools/PDFTools/PdfToJpg').then(module => ({ default: module.PdfToJpg })));
const PdfMetadata = React.lazy(() => import('../components/Tools/PDFTools/PdfMetadata').then(module => ({ default: module.PdfMetadata })));
const TxtToPdf = React.lazy(() => import('../components/Tools/PDFTools/TxtToPdf').then(module => ({ default: module.TxtToPdf })));
const TextAnalyzer = React.lazy(() => import('../components/Tools/TextTools/TextAnalyzer').then(module => ({ default: module.TextAnalyzer })));
const BionicReading = React.lazy(() => import('../components/Tools/TextTools/BionicReading').then(module => ({ default: module.BionicReading })));
const TextCapitalizer = React.lazy(() => import('../components/Tools/TextTools/TextCapitalizer').then(module => ({ default: module.TextCapitalizer })));
const UppercaseConverter = React.lazy(() => import('../components/Tools/TextTools/UppercaseConverter').then(module => ({ default: module.UppercaseConverter })));
const LowercaseConverter = React.lazy(() => import('../components/Tools/TextTools/LowercaseConverter').then(module => ({ default: module.LowercaseConverter })));
const CamelCaseConverter = React.lazy(() => import('../components/Tools/TextTools/CamelCaseConverter').then(module => ({ default: module.CamelCaseConverter })));
const DuplicateLineRemover = React.lazy(() => import('../components/Tools/TextTools/DuplicateLineRemover').then(module => ({ default: module.DuplicateLineRemover })));
const TextSorter = React.lazy(() => import('../components/Tools/TextTools/TextSorter').then(module => ({ default: module.TextSorter })));
const Base64Encoder = React.lazy(() => import('../components/Tools/EncryptionTools/Base64Encoder').then(module => ({ default: module.Base64Encoder })));
const MD5HashGenerator = React.lazy(() => import('../components/Tools/EncryptionTools/MD5HashGenerator').then(module => ({ default: module.MD5HashGenerator })));
const SHA1HashGenerator = React.lazy(() => import('../components/Tools/EncryptionTools/SHA1HashGenerator').then(module => ({ default: module.SHA1HashGenerator })));
const SHA256HashGenerator = React.lazy(() => import('../components/Tools/EncryptionTools/SHA256HashGenerator').then(module => ({ default: module.SHA256HashGenerator })));
const SHA512HashGenerator = React.lazy(() => import('../components/Tools/EncryptionTools/SHA512HashGenerator').then(module => ({ default: module.SHA512HashGenerator })));
const URLEncoder = React.lazy(() => import('../components/Tools/EncryptionTools/URLEncoder').then(module => ({ default: module.URLEncoder })));
const HTMLEncoder = React.lazy(() => import('../components/Tools/EncryptionTools/HTMLEncoder').then(module => ({ default: module.HTMLEncoder })));
const NumberToWords = React.lazy(() => import('../components/Tools/ConverterTools/NumberToWords').then(module => ({ default: module.NumberToWords })));
const BinaryConverter = React.lazy(() => import('../components/Tools/ConverterTools/BinaryConverter').then(module => ({ default: module.BinaryConverter })));
const HexadecimalConverter = React.lazy(() => import('../components/Tools/ConverterTools/HexadecimalConverter').then(module => ({ default: module.HexadecimalConverter })));
const OctalConverter = React.lazy(() => import('../components/Tools/ConverterTools/OctalConverter').then(module => ({ default: module.OctalConverter })));
const ASCIIConverter = React.lazy(() => import('../components/Tools/ConverterTools/ASCIIConverter').then(module => ({ default: module.ASCIIConverter })));
const BinaryTextConverter = React.lazy(() => import('../components/Tools/ConverterTools/BinaryTextConverter').then(module => ({ default: module.BinaryTextConverter })));
const QRCodeGenerator = React.lazy(() => import('../components/Tools/ConverterTools/QRCodeGenerator').then(module => ({ default: module.QRCodeGenerator })));
const CSVJSONConverter = React.lazy(() => import('../components/Tools/ConverterTools/CSVJSONConverter').then(module => ({ default: module.CSVJSONConverter })));
const MarkdownToHtml = React.lazy(() => import('../components/Tools/ConverterTools/MarkdownToHtml').then(module => ({ default: module.MarkdownToHtml })));
const MarkdownPreview = React.lazy(() => import('../components/Tools/ConverterTools/MarkdownPreview').then(module => ({ default: module.MarkdownPreview })));
const IPToBinary = React.lazy(() => import('../components/Tools/ConverterTools/IPToBinary').then(module => ({ default: module.IPToBinary })));
const XMLFormatter = React.lazy(() => import('../components/Tools/ConverterTools/XMLFormatter').then(module => ({ default: module.XMLFormatter })));
const NumberBaseConverter = React.lazy(() => import('../components/Tools/ConverterTools/NumberBaseConverter').then(module => ({ default: module.NumberBaseConverter })));
const TimezoneConverter = React.lazy(() => import('../components/Tools/ConverterTools/TimezoneConverter').then(module => ({ default: module.TimezoneConverter })));

export const ToolPage: React.FC = () => {
  const { toolId } = useParams<{ toolId: string }>();
  const tools = getAllTools();
  const tool = tools.find(t => t.id === toolId);

  if (!tool) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Tool not found</h1>
          <Link to="/" className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300">
            Return to home
          </Link>
        </div>
      </div>
    );
  }

  // Component mapping
  const getToolComponent = (toolId: string) => {
    switch (toolId) {
      // Image Tools
      case 'webp-to-jpg':
        return <WebpToJpg />;
      case 'png-to-jpg':
        return <PngToJpg />;
      case 'jpg-to-png':
        return <JpgToPng />;
      case 'image-compressor':
        return <ImageCompressor />;
      case 'image-cropper':
        return <ImageCropper />;
      case 'image-to-base64':
        return <ImageToBase64 />;
      case 'base64-to-image':
        return <Base64ToImage />;
      case 'image-color-inverter':
        return <ImageColorInverter />;
      
      // PDF Tools
      case 'pdf-merger':
        return <PdfMerger />;
      case 'jpg-to-pdf':
        return <JpgToPdf />;
      case 'pdf-to-jpg':
        return <PdfToJpg />;

      case 'pdf-metadata':
        return <PdfMetadata />;
                case 'txt-to-pdf':
            return <TxtToPdf />;
      
      // Text Tools
      case 'text-analyzer':
        return <TextAnalyzer />;
      case 'bionic-reading':
        return <BionicReading />;
      case 'text-capitalizer':
        return <TextCapitalizer />;
      case 'uppercase-converter':
        return <UppercaseConverter />;
      case 'lowercase-converter':
        return <LowercaseConverter />;
      case 'camelcase-converter':
        return <CamelCaseConverter />;
      case 'duplicate-remover':
        return <DuplicateLineRemover />;
      case 'text-sorter':
        return <TextSorter />;
      
              // Encryption Tools
        case 'base64-encoder':
            return <Base64Encoder />;
        case 'md5-hash':
            return <MD5HashGenerator />;
        case 'sha1-hash':
            return <SHA1HashGenerator />;
        case 'sha256-hash':
            return <SHA256HashGenerator />;
        case 'sha512-hash':
            return <SHA512HashGenerator />;
        case 'url-encoder':
            return <URLEncoder />;
        case 'html-encoder':
            return <HTMLEncoder />;
            
        // Converter Tools
        case 'numbers-to-words':
            return <NumberToWords />;
        case 'number-base-converter':
            return <NumberBaseConverter />;
        case 'timezone-converter':
            return <TimezoneConverter />;
        case 'qr-generator':
            return <QRCodeGenerator />;
        case 'binary-converter':
            return <BinaryConverter />;
        case 'hexadecimal-converter':
            return <HexadecimalConverter />;
        case 'octal-converter':
            return <OctalConverter />;
        case 'ascii-converter':
            return <ASCIIConverter />;
        case 'binary-text-converter':
            return <BinaryTextConverter />;
        case 'csv-json-converter':
            return <CSVJSONConverter />;
        case 'markdown-to-html':
            return <MarkdownToHtml />;
        case 'markdown-preview':
            return <MarkdownPreview />;
        case 'ip-to-binary':
            return <IPToBinary />;
        case 'xml-formatter':
            return <XMLFormatter />;
      
      default:
        return (
          <div className="max-w-4xl mx-auto p-6">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-8 text-center">
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">{tool.name}</h1>
              <p className="text-gray-600 dark:text-gray-400 mb-8">{tool.description}</p>
              <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-6">
                <p className="text-yellow-800 dark:text-yellow-400">
                  🚧 This tool is coming soon! We're working hard to bring you this feature.
                </p>
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <div className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400 mb-6">
          <Link to="/" className="hover:text-blue-600 dark:hover:text-blue-400">Home</Link>
          <span>/</span>
          <Link to={`/category/${tool.category}`} className="hover:text-blue-600 dark:hover:text-blue-400">
            {tool.category.charAt(0).toUpperCase() + tool.category.slice(1)}
          </Link>
          <span>/</span>
          <span className="text-gray-900 dark:text-white">{tool.name}</span>
        </div>

        {/* Back Button */}
        <Link
          to={`/category/${tool.category}`}
          className="inline-flex items-center space-x-2 text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 mb-8"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>Back to {tool.category} tools</span>
        </Link>

        {/* Tool Header */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-8 mb-8 border border-gray-200 dark:border-gray-700">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            {tool.name}
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-400 mb-6">
            {tool.description}
          </p>
          
          {/* Privacy Notice */}
          <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
            <p className="text-green-800 dark:text-green-400 text-sm">
              🔒 This tool processes your data locally in your browser. 
              Your files never leave your device, ensuring complete privacy and security.
            </p>
          </div>
        </div>

        {/* Top Display Ad */}
        <DisplayAd className="mb-8" />

        {/* Tool Description with In-article Ad */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-8 mb-8 border border-gray-200 dark:border-gray-700">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            About {tool.name}
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {tool.description} This powerful tool provides professional-grade functionality 
            while maintaining complete privacy through client-side processing.
          </p>
          
          {/* In-article Ad */}
          <InArticleAd />
          
          <div className="mt-6">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">
              Key Features
            </h3>
            <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 space-y-2">
              <li>100% client-side processing</li>
              <li>No file uploads to servers</li>
              <li>Complete privacy protection</li>
              <li>Professional-grade results</li>
              <li>Works on all devices</li>
            </ul>
          </div>
        </div>

        {/* Tool Component */}
        <Suspense fallback={
          <div className="max-w-4xl mx-auto p-6">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-8 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600 dark:text-gray-400">Loading tool...</p>
            </div>
          </div>
        }>
          {getToolComponent(toolId!)}
        </Suspense>

        {/* Bottom Display Ad */}
        <DisplayAd className="mt-8" />
      </div>
    </div>
  );
};